import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { CollapsePlacement } from '@/lib/collapse';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  A SECONDARY COLUMN THAT BECOMES A SUMMARY BAR BELOW ITS THRESHOLD. [UX-FINAL]
 *
 *  A plain disclosure: one button, one boolean, one body. It reads NO width, mounts
 *  nothing conditionally and renders its children exactly once — read `lib/collapse.ts`
 *  for why all three are non-negotiable rather than merely tidy.
 *
 *  ⚠️ IT OWNS NO POSITION. Where the bar pins, and to which edge, is the HOST's question,
 *  so every positioning class arrives as `placement`. See `CollapsePlacement.root`.
 *
 *  ⚠️ THE BAR IS ALWAYS IN THE DOM, INCLUDING ABOVE THE SPLIT, where a stylesheet removes
 *  it. Under jsdom it is therefore always present and always focusable. That is safe only
 *  because `label` is a real, specific noun phrase: there is no `getAllByRole('button')`
 *  count anywhere in the web suite, but there are dozens of `getByRole('button', { name })`.
 *
 *  ⚠️ NO SCRIM AND NO FOCUS TRAP, on purpose. Those would make it a dialog, and a dialog is
 *  the thing that unmounts. It is a disclosure: `aria-expanded` carries the state, so the
 *  button's name is the SECTION's name and never "Show…"/"Hide…", which would fight it.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type CollapsiblePanelProps = {
  /** The disclosure's accessible name AND the bar's visible text. A noun phrase. */
  label: string;
  placement: CollapsePlacement;
  children: ReactNode;
  /**
   * [P7B.1 §2] Closes the panel when this value changes — a tree that opened a pane must not stay open
   * over it once something was picked. Omit it and nothing changes.
   */
  closeOn?: unknown;
};

export const CollapsiblePanel = ({ label, placement, children, closeOn }: CollapsiblePanelProps) => {
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  /*
   * ⚠️ ADJUSTED WHILE RENDERING, NEVER IN AN EFFECT — React's "storing information from previous renders":
   * an effect would paint the stale open panel first and `react-hooks/set-state-in-effect` refuses it.
   */
  const [closedFor, setClosedFor] = useState(closeOn);
  if (!Object.is(closedFor, closeOn)) {
    setClosedFor(closeOn);
    setOpen(false);
  }

  return (
    <div
      className={cn('flex min-w-0 flex-col', placement.root)}
      data-testid="collapsible-panel"
      /*
       * Escape closes it — local, not a document listener, so it cannot reach past this
       * subtree. ⚠️ Guarded on `open`, which is `false` on first render and which nothing in
       * the unit suite ever flips, so this handler is inert in every web test rather than
       * "probably harmless".
       */
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (open && event.key === 'Escape') setOpen(false);
      }}
    >
      {/*
        ⚠️ `type="button"`. `IntakeForm` renders this inside an AntD `<Form>`, where a bare
        button submits — the same trap `IntakeForm`'s own stepper header records.
      */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        data-testid="collapsible-panel-toggle"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex w-full shrink-0 items-center justify-between gap-2 border-border bg-card px-3 py-2',
          'text-left text-sm font-medium hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          placement.bar,
        )}
      >
        {label}
        <ChevronDown className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {/*
        ⚠️ ONE COPY OF THE CHILDREN, AND NOTHING IS SUMMARISED ONTO THE BAR. A count on the
        bar would be a SECOND copy of a string the body already renders, and `getByText`
        throws on two matches. If a summary is ever wanted it has to be a value that appears
        nowhere in the body, decided in a commit of its own.
      */}
      <div
        id={bodyId}
        data-testid="collapsible-panel-body"
        className={cn('min-w-0', placement.body, !open && placement.collapsed)}
      >
        {children}
      </div>
    </div>
  );
};
